import { useCallback, useEffect, useRef, useState } from 'react';
import { getData } from '../utils/httpClient';

const CACHE_TTL_MS = 30 * 60 * 1000; // 30 min
let memoryCache = {
  rate: null,
  date: null,
  fetchedAt: 0,
  payload: null,
};

/**
 * Cotización USD (venta BCU). Cache en memoria para reutilizar entre pantallas.
 */
export default function useBcuUsdRate(date) {
  const [rate, setRate] = useState(memoryCache.rate);
  const [info, setInfo] = useState(memoryCache.payload);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestId = useRef(0);

  const fetchRate = useCallback(
    async ({ force = false } = {}) => {
      const targetDate = date || null;
      const cacheFresh =
        memoryCache.rate &&
        Date.now() - memoryCache.fetchedAt < CACHE_TTL_MS &&
        memoryCache.date === targetDate;

      if (!force && cacheFresh) {
        setRate(memoryCache.rate);
        setInfo(memoryCache.payload);
        setError(null);
        return memoryCache.rate;
      }

      const id = ++requestId.current;
      setLoading(true);
      setError(null);
      try {
        const qs = targetDate
          ? `?date=${encodeURIComponent(targetDate)}`
          : '';
        const res = await getData(`/currencies/bcu/usd${qs}`, {
          skipLoader: true,
        });
        const nextRate = Number(res?.payload?.rate);
        if (!Number.isFinite(nextRate) || nextRate <= 0) {
          throw new Error('Cotización BCU inválida');
        }

        memoryCache = {
          rate: nextRate,
          date: targetDate,
          fetchedAt: Date.now(),
          payload: res.payload,
        };

        if (id === requestId.current) {
          setRate(nextRate);
          setInfo(res.payload);
        }
        return nextRate;
      } catch (err) {
        // Fallback: rate guardado en catálogo de monedas
        try {
          const currenciesRes = await getData('/currencies', {
            skipLoader: true,
          });
          const usd = (currenciesRes?.payload?.currencies || []).find(
            (c) => String(c.code).toUpperCase() === 'USD',
          );
          const fallback = Number(usd?.rate);
          if (Number.isFinite(fallback) && fallback > 0) {
            memoryCache = {
              rate: fallback,
              date: targetDate,
              fetchedAt: Date.now(),
              payload: {
                rate: fallback,
                source: 'DB',
                currency: 'USD',
              },
            };
            if (id === requestId.current) {
              setRate(fallback);
              setInfo(memoryCache.payload);
              setError(err?.message || 'Usando cotización local');
            }
            return fallback;
          }
        } catch {
          // ignore
        }

        if (id === requestId.current) {
          setError(err?.message || 'No se pudo obtener cotización BCU');
        }
        return null;
      } finally {
        if (id === requestId.current) setLoading(false);
      }
    },
    [date],
  );

  useEffect(() => {
    fetchRate();
  }, [fetchRate]);

  return {
    rate,
    info,
    loading,
    error,
    refresh: () => fetchRate({ force: true }),
  };
}
